/**
 * Mesh-conforming selection outline — clones the object's render meshes, scales
 * them slightly, and draws back faces only so the shell follows the real shape.
 */

import * as pc from "playcanvas";

export const OUTLINE_ENTITY_NAME = "selection-outline";
const OUTLINE_SCALE = 1.07;
const OUTLINE_DRAW_ORDER = 100;

const OUTLINE_RGB = [0.43, 0.66, 1.0];

export function createOutlineMaterial() {
  const mat = new pc.StandardMaterial();
  mat.useLighting = false;
  mat.diffuse = new pc.Color(0, 0, 0);
  mat.emissive = new pc.Color(...OUTLINE_RGB);
  mat.emissiveIntensity = 1.35;
  mat.opacity = 1;
  mat.cull = pc.CULLFACE_FRONT;
  mat.depthWrite = false;
  mat.depthTest = true;
  // Pull the outline slightly toward the camera so it wins over the mesh + floor.
  mat.depthBias = -4;
  mat.slopeDepthBias = -2;
  mat.blendType = pc.BLEND_NORMAL;
  mat.update();
  return mat;
}

function addMeshOutline(srcEntity, dstParent, material) {
  const render = srcEntity.render;
  if (!render?.meshInstances?.length) return;

  const node = new pc.Entity(`${srcEntity.name || "mesh"}_ol`);
  const meshInstances = render.meshInstances.map((mi) => {
    const outline = new pc.MeshInstance(mi.mesh, material, node);
    outline.drawOrder = OUTLINE_DRAW_ORDER;
    return outline;
  });
  node.addComponent("render", {
    meshInstances,
    castShadows: false,
    receiveShadows: false,
  });
  dstParent.addChild(node);
}

function cloneOutlineTree(srcEntity, dstParent, material) {
  for (const child of srcEntity.children) {
    if (child.name === OUTLINE_ENTITY_NAME) continue;

    const node = new pc.Entity(`${child.name || "node"}_ol`);
    node.setLocalPosition(child.getLocalPosition());
    node.setLocalRotation(child.getLocalRotation());
    node.setLocalScale(child.getLocalScale());
    dstParent.addChild(node);

    addMeshOutline(child, node, material);
    cloneOutlineTree(child, node, material);
  }
}

/** Attach a shape-following outline as a child of `entity`. */
export function attachSelectionOutline(entity, material, scale = OUTLINE_SCALE) {
  if (!entity) return null;
  detachSelectionOutline(entity);

  const root = new pc.Entity(OUTLINE_ENTITY_NAME);
  root.setLocalScale(scale, scale, scale);

  addMeshOutline(entity, root, material);
  cloneOutlineTree(entity, root, material);
  entity.addChild(root);
  return root;
}

export function detachSelectionOutline(entity) {
  const existing = entity?.findByName?.(OUTLINE_ENTITY_NAME);
  existing?.destroy();
}

export function hasSelectionOutline(entity) {
  return Boolean(entity?.findByName?.(OUTLINE_ENTITY_NAME));
}

/** Gentle pulse so the selected outline reads clearly against the scene. */
export function pulseOutlineMaterial(mat, t) {
  const phase = (Math.sin(t * 3.2) + 1) / 2;
  mat.emissiveIntensity = 0.85 + phase * 0.95;
  mat.update();
}
